import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Trash2, ShoppingBag, Check, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n);

const SHIPPING_THRESHOLD = 45;
const SHIPPING_COST = 4.9;

const Carrinho = () => {
  const { items, subtotal, removeItem, updateQty, clear } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    postal_code: "",
    city: "",
    notes: "",
  });

  const shipping = subtotal >= SHIPPING_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_COST;
  const total = subtotal + shipping;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map(({ product_id, name, price, quantity, image }) => ({
          product_id,
          name,
          price,
          quantity,
          image,
        })),
        total,
      };
      const res = await axios.post(`${API}/orders`, payload);
      setConfirmedOrder(res.data);
      clear();
      toast.success("Encomenda registada com sucesso!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao registar encomenda.");
    } finally {
      setSubmitting(false);
    }
  };

  // CONFIRMATION
  if (confirmedOrder) {
    return (
      <div data-testid="order-confirmation" className="container-luxe py-20 max-w-2xl text-center">
        <div className="w-20 h-20 bg-gold-bright text-ink rounded-full flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(255,215,0,0.4)]">
          <Check className="w-10 h-10" strokeWidth={2} />
        </div>
        <p className="overline mt-8 mb-4">— Encomenda confirmada</p>
        <h1 className="font-serif text-5xl text-ink">
          Obrigada, <span className="italic text-gold">{confirmedOrder.customer_name.split(" ")[0]}</span>.
        </h1>
        <p className="mt-6 text-ink/70 leading-relaxed">
          A sua encomenda <span className="font-mono text-gold">#{confirmedOrder.id.slice(0, 8)}</span> foi
          recebida. Entraremos em contacto consigo brevemente para confirmar os detalhes de pagamento e envio.
        </p>
        <div className="mt-8 bg-beige-light p-8 text-left">
          <p className="overline mb-3">Resumo</p>
          <ul className="divide-y divide-gold/15">
            {confirmedOrder.items.map((it) => (
              <li key={it.product_id} className="py-3 flex justify-between text-sm">
                <span>{it.name} <span className="text-ink/50">× {it.quantity}</span></span>
                <span className="tabular-nums">{fmt(it.price * it.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-gold/20 mt-4 pt-4 flex justify-between font-serif text-xl">
            <span>Total</span>
            <span className="text-gold">{fmt(confirmedOrder.total)}</span>
          </div>
        </div>
        <button onClick={() => navigate("/loja")} data-testid="order-continue-btn" className="btn-outline-gold mt-10">
          Continuar a explorar
        </button>
      </div>
    );
  }

  // EMPTY
  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="container-luxe py-24 max-w-xl text-center">
        <ShoppingBag className="w-14 h-14 text-gold/40 mx-auto" strokeWidth={1} />
        <h1 className="font-serif text-5xl text-ink mt-8">
          Carrinho <span className="italic text-gold">vazio</span>
        </h1>
        <p className="mt-4 text-ink/60">Adicione produtos para começar a sua encomenda.</p>
        <Link to="/loja" data-testid="cart-empty-back-btn" className="btn-gold mt-10 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Explorar produtos
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="carrinho-page" className="container-luxe py-12 pb-24">
      <p className="overline mb-4">— Finalizar</p>
      <h1 className="font-serif text-5xl sm:text-6xl font-light text-ink mb-12">
        O seu <span className="italic text-gold">carrinho</span>
      </h1>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Items + form */}
        <div className="lg:col-span-7 space-y-12">
          {/* Items */}
          <section>
            <h2 className="overline mb-4">Produtos</h2>
            <ul className="divide-y divide-gold/15 border-t border-b border-gold/15">
              {items.map((it) => (
                <li key={it.product_id} className="py-5 flex gap-4" data-testid={`checkout-item-${it.product_id}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-beige" />
                  <div className="flex-1 flex flex-col">
                    <p className="overline text-gold">{it.line}</p>
                    <p className="font-serif text-lg text-ink">{it.name}</p>
                    <p className="text-xs text-ink/50">{it.size}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-gold/30">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2 hover:bg-gold/10" aria-label="Diminuir">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm">{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2 hover:bg-gold/10" aria-label="Aumentar">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-gold font-medium tabular-nums">{fmt(it.price * it.quantity)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-ink/40 hover:text-gold self-start p-1" aria-label="Remover">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
            <h2 className="overline">Dados de envio</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Nome completo *</label>
                <input
                  name="customer_name"
                  required
                  value={form.customer_name}
                  onChange={handleChange}
                  data-testid="checkout-name"
                  className="input-luxe"
                  placeholder="O seu nome"
                />
              </div>
              <div>
                <label className="overline block mb-2">Telefone *</label>
                <input
                  name="customer_phone"
                  required
                  value={form.customer_phone}
                  onChange={handleChange}
                  data-testid="checkout-phone"
                  className="input-luxe"
                  placeholder="+351 ___ ___ ___"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Email *</label>
                <input
                  name="customer_email"
                  type="email"
                  required
                  value={form.customer_email}
                  onChange={handleChange}
                  data-testid="checkout-email"
                  className="input-luxe"
                  placeholder="email@exemplo.pt"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Morada *</label>
                <input
                  name="address"
                  required
                  value={form.address}
                  onChange={handleChange}
                  data-testid="checkout-address"
                  className="input-luxe"
                  placeholder="Rua, número, andar"
                />
              </div>
              <div>
                <label className="overline block mb-2">Código postal *</label>
                <input
                  name="postal_code"
                  required
                  value={form.postal_code}
                  onChange={handleChange}
                  data-testid="checkout-postal"
                  className="input-luxe"
                  placeholder="0000-000"
                />
              </div>
              <div>
                <label className="overline block mb-2">Localidade *</label>
                <input
                  name="city"
                  required
                  value={form.city}
                  onChange={handleChange}
                  data-testid="checkout-city"
                  className="input-luxe"
                  placeholder="Cidade"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Notas (opcional)</label>
                <textarea
                  name="notes"
                  rows={3}
                  value={form.notes}
                  onChange={handleChange}
                  data-testid="checkout-notes"
                  className="input-luxe resize-none"
                  placeholder="Informações adicionais para a entrega"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="bg-beige-light p-8 lg:sticky lg:top-28 border-t-2 border-gold">
            <h2 className="font-serif text-3xl text-ink mb-6">Resumo</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/70">Subtotal</dt>
                <dd className="tabular-nums" data-testid="summary-subtotal">{fmt(subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/70">Portes</dt>
                <dd className="tabular-nums" data-testid="summary-shipping">
                  {shipping === 0 ? <span className="text-gold">Grátis</span> : fmt(shipping)}
                </dd>
              </div>
              {subtotal < SHIPPING_THRESHOLD && (
                <p className="text-xs text-ink/50 italic">
                  Faltam {fmt(SHIPPING_THRESHOLD - subtotal)} para portes grátis.
                </p>
              )}
            </dl>
            <div className="border-t border-gold/30 mt-5 pt-5 flex justify-between items-baseline">
              <span className="overline">Total</span>
              <span className="font-serif text-3xl text-gold tabular-nums" data-testid="summary-total">{fmt(total)}</span>
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || items.length === 0}
              data-testid="submit-order-btn"
              className="btn-gold w-full mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> A registar...</>
              ) : (
                <>Confirmar Encomenda</>
              )}
            </button>

            <p className="text-xs text-ink/50 mt-6 leading-relaxed">
              ✦ Ao confirmar, registamos a sua encomenda. Entraremos em contacto para
              acertar o pagamento (transferência ou MB Way) e envio. Sem cobrança imediata.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Carrinho;
